/**
 * Callback Flow Manager - Multi-Provider OAuth Flow Protection
 * Prevents providers from interfering with each other's active OAuth callbacks
 */

import { logger } from '@/services/LoggingService';

export type ProviderType = 'wealthica2' | 'vezgo' | 'demo' | 'manual';

export interface CallbackFlowState {
  provider: ProviderType;
  isActive: boolean;
  callbackPath: string;
  detectedAt: string;
  parameters: Record<string, string>;
}

/**
 * Provider-specific callback patterns for accurate detection
 */
const PROVIDER_CALLBACK_PATTERNS: Record<ProviderType, (url: Location) => CallbackFlowState | null> = {
  wealthica2: (url: Location) => {
    const isWealthicaCallback = 
      url.pathname.includes('/investments/connect') &&
      (url.search.includes('provider=wealthsimple') || 
       url.search.includes('institution=')) &&
      url.search.includes('state=');

    if (!isWealthicaCallback) return null;

    const params = new URLSearchParams(url.search);
    return {
      provider: 'wealthica2',
      isActive: true,
      callbackPath: url.pathname + url.search,
      detectedAt: new Date().toISOString(),
      parameters: {
        institution: params.get('institution') || '',
        provider: params.get('provider') || '',
        state: params.get('state') || '',
        code: params.get('code') || ''
      }
    };
  },

  vezgo: (url: Location) => {
    const isVezgoCallback = 
      url.pathname.includes('/investments/connect/vezgo') &&
      url.search.includes('state=');

    if (!isVezgoCallback) return null;

    const params = new URLSearchParams(url.search);
    return {
      provider: 'vezgo',
      isActive: true,
      callbackPath: url.pathname + url.search,
      detectedAt: new Date().toISOString(),
      parameters: {
        state: params.get('state') || '',
        account: params.get('account') || '',
        provider: params.get('provider') || ''
      }
    };
  },

  demo: () => null, // Demo provider doesn't use OAuth callbacks
  manual: () => null // Manual provider doesn't use OAuth callbacks
};

/**
 * Centralized callback flow manager
 */
export class CallbackFlowManager {
  private static instance: CallbackFlowManager;
  private activeFlows: Map<ProviderType, CallbackFlowState> = new Map();

  private constructor() {}

  static getInstance(): CallbackFlowManager {
    if (!CallbackFlowManager.instance) {
      CallbackFlowManager.instance = new CallbackFlowManager();
    }
    return CallbackFlowManager.instance;
  }

  /**
   * Detect and register all active callback flows
   */
  detectActiveFlows(): CallbackFlowState[] {
    try {
      const currentUrl = window.location;
      const detectedFlows: CallbackFlowState[] = [];

      // Clear previous flows
      this.activeFlows.clear();

      // Check each provider for active callbacks
      Object.entries(PROVIDER_CALLBACK_PATTERNS).forEach(([provider, detector]) => {
        const flow = detector(currentUrl);
        if (flow) {
          this.activeFlows.set(provider as ProviderType, flow);
          detectedFlows.push(flow);
        }
      });

      if (detectedFlows.length > 0) {
        logger.info('CallbackFlowManager', 'Active OAuth callback flows detected', {
          flowCount: detectedFlows.length,
          flows: detectedFlows.map(flow => ({
            provider: flow.provider,
            callbackPath: flow.callbackPath
          }))
        });
      }

      return detectedFlows;
    } catch (error) {
      logger.error('CallbackFlowManager', 'Failed to detect active flows', { error });
      return [];
    }
  }

  /**
   * Check if a specific provider has an active callback flow
   */
  isProviderInCallback(provider: ProviderType): boolean {
    return this.activeFlows.has(provider);
  }

  /**
   * Check if ANY provider has an active callback flow
   */
  hasActiveCallbacks(): boolean {
    return this.activeFlows.size > 0;
  }

  /**
   * Get all active callback flows
   */
  getActiveFlows(): CallbackFlowState[] {
    return Array.from(this.activeFlows.values());
  }

  /**
   * Get callback flow for specific provider
   */
  getProviderFlow(provider: ProviderType): CallbackFlowState | null {
    return this.activeFlows.get(provider) || null;
  }

  /**
   * CRITICAL: Check if it's safe for a provider to perform cleanup operations
   * Returns false if ANY provider is in an active callback to prevent interference
   */
  isSafeForCleanup(requestingProvider: ProviderType): boolean {
    const activeFlows = this.detectActiveFlows();
    
    if (activeFlows.length === 0) {
      return true; // No active flows, safe to cleanup
    }

    // If requesting provider is in callback, allow its own cleanup
    const isRequestingProviderInCallback = activeFlows.some(flow => flow.provider === requestingProvider);
    
    if (isRequestingProviderInCallback && activeFlows.length === 1) {
      return true; // Only requesting provider is active
    }

    // Other providers are active, not safe for cleanup
    logger.warn('CallbackFlowManager', 'Cleanup blocked - active callback flows detected', {
      requestingProvider,
      activeFlows: activeFlows.map(flow => flow.provider)
    });

    return false;
  }

  /**
   * Provider-aware initialization check
   */
  isSafeForInitialization(requestingProvider: ProviderType): boolean {
    return this.isSafeForCleanup(requestingProvider);
  }

  /**
   * Clear flow state when callback processing completes
   */
  clearProviderFlow(provider: ProviderType): void {
    if (this.activeFlows.has(provider)) {
      logger.info('CallbackFlowManager', 'Clearing completed callback flow', { provider });
      this.activeFlows.delete(provider);
    }
  }

  /**
   * Emergency clear all flows (use with caution)
   */
  clearAllFlows(): void {
    logger.warn('CallbackFlowManager', 'Emergency clearing all callback flows');
    this.activeFlows.clear();
  }
}

/**
 * Utility functions for provider isolation
 */
export const CallbackUtils = {
  /**
   * Check if it's safe for a provider to perform operations
   */
  isSafeForProviderOperation(provider: ProviderType, operation: string): boolean {
    const manager = CallbackFlowManager.getInstance();
    const isSafe = manager.isSafeForCleanup(provider);

    if (!isSafe) {
      logger.warn('CallbackUtils', `${operation} blocked for provider due to active callbacks`, {
        provider,
        operation,
        activeFlows: manager.getActiveFlows().map(flow => flow.provider)
      });
    }

    return isSafe;
  },

  /**
   * Get detailed callback information for debugging
   */
  getCallbackDebugInfo(): Record<string, any> {
    const manager = CallbackFlowManager.getInstance();
    const activeFlows = manager.detectActiveFlows();

    return {
      hasActiveCallbacks: activeFlows.length > 0,
      activeFlowCount: activeFlows.length,
      flows: activeFlows,
      currentUrl: {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash
      }
    };
  }
};

/**
 * Decorator to protect provider methods from cross-provider interference
 */
export function ProtectedProviderOperation(provider: ProviderType) {
  return function (target: any, propertyName: string, descriptor: PropertyDescriptor) {
    const method = descriptor.value;

    descriptor.value = function (...args: any[]) {
      const isSafe = CallbackUtils.isSafeForProviderOperation(provider, propertyName);
      
      if (!isSafe) {
        logger.warn('ProtectedProviderOperation', `Skipping ${propertyName} due to active callbacks`, {
          provider,
          method: propertyName
        });
        return null;
      }

      return method.apply(this, args);
    };
  };
}
